import type {
  ICreateCalendarLinkPayload,
  ICreateCalendarLinkResponse,
  ICreateExchangeConnectionPayload,
  ICreateExchangeConnectionResponse,
  IGetCalendarEventsResponse,
  IGetCalendarsResponse,
  IRedirectToGoogleParams,
  IRedirectToGoogleResponse,
  IRedirectToMicrosoftParams,
  IRedirectToMicrosoftResponse,
  IRefreshCalendarEventsResponse,
  IRenameCalendarPayload,
  IUpdateCalendarColorPayload,
  IUpdateCalendarEventParams,
} from "../types/calendar";
import type { ApiClient } from "./client";

export class CalendarApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getCalendars(): Promise<IGetCalendarsResponse> {
    const response = await this.apiClient.get<IGetCalendarsResponse>(
      "/calendar/calendars",
    );
    return response.data;
  }

  async getCalendarEvents(): Promise<IGetCalendarEventsResponse> {
    const response = await this.apiClient.get<IGetCalendarEventsResponse>(
      "/calendar/calendar-events",
    );
    return response.data;
  }

  async refreshCalendarEvents(): Promise<IRefreshCalendarEventsResponse> {
    const response = await this.apiClient.post<IRefreshCalendarEventsResponse>(
      "/calendar/refresh-events",
    );
    return response.data;
  }

  async redirectToGoogle({
    redirectTo,
  }: IRedirectToGoogleParams): Promise<IRedirectToGoogleResponse> {
    const response = await this.apiClient.post<IRedirectToGoogleResponse>(
      "/calendar/google",
      { redirectTo },
    );
    return response.data;
  }

  async redirectToMicrosoft({
    redirectTo,
  }: IRedirectToMicrosoftParams): Promise<IRedirectToMicrosoftResponse> {
    const response = await this.apiClient.post<IRedirectToMicrosoftResponse>(
      "/calendar/microsoft",
      { redirectTo },
    );
    return response.data;
  }

  async createCalendarLink(
    payload: ICreateCalendarLinkPayload,
  ): Promise<ICreateCalendarLinkResponse> {
    const response = await this.apiClient.post<ICreateCalendarLinkResponse>(
      "/calendar/calendar-link",
      payload,
    );
    return response.data;
  }

  async createExchangeConnection(
    payload: ICreateExchangeConnectionPayload,
  ): Promise<ICreateExchangeConnectionResponse> {
    const response =
      await this.apiClient.post<ICreateExchangeConnectionResponse>(
        "/calendar/exchange",
        payload,
      );
    return response.data;
  }

  async updateCalendarColor({
    uuid,
    color,
  }: IUpdateCalendarColorPayload): Promise<void> {
    await this.apiClient.patch(`/calendar/${uuid}/color`, { color });
  }

  async renameCalendar({
    uuid,
    displayName,
  }: IRenameCalendarPayload): Promise<void> {
    await this.apiClient.patch(`/calendar/${uuid}/name`, { displayName });
  }

  async deleteCalendar(uuid: string): Promise<void> {
    await this.apiClient.delete(`/calendar/${uuid}`);
  }

  async updateCalendarEvent({
    calendarEventUuid,
    payload,
  }: IUpdateCalendarEventParams): Promise<void> {
    await this.apiClient.put(`/calendar/${calendarEventUuid}`, payload);
  }
}
